import React from 'react';
import { combineLatest, concat, firstValueFrom, from, map, merge } from 'rxjs';
import type { AnySchema } from 'yup';
import { BaseReportElement } from '../BaseReportElement';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementSettingsFormSchema,
    ElementSettings,
    ElementContentRenderResult,
    ElementRenderState,
    RenderContextKind,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../types';
import { DynamicTableRenderer } from './DynamicTableRenderer';
import { buildTableSettingsYupSchema, normalizeTableSettings } from './settingsYupSchema';
import { createBlankTableDefinition, createRuntimeTableInsertionPresets } from './presets';
import { shouldRenderTable, renderTableLayout } from './renderTableLayout';
import {
    buildLinkTableShape,
    getTableRuntimeBindingDescriptorOrThrow,
    listTableRuntimeBindingsForContext,
} from './runtimeBinding';
import { createDefaultStaticTableSettings, type TableSettings } from './contracts';
import {
    linkNeedsDataSet,
    resolveCellRenderOptions$,
} from '../../runtime/dataServices/cellRenderOptions';
import {
    createRuntimeBindingGroupField,
    resolveRuntimeBindingSettingsFormSchema,
    withRuntimeBindingArgChoiceField,
} from '../runtimeBindingFormUtils';
import {
    PROJECT_MEASURING_POINTS_BINDING_ID,
    resolveProjectArgOptions,
} from '../../runtime/dataServices/projectMeasuringPoints/argOptions';
import { isRecord } from '../../../../../utils/typeGuards';

const TABLE_STATIC_LAYOUT_FIELD_NAMES = new Set([
    'header',
    'columnAlignments',
    'columnWidths',
    'rows',
]);

function renderTableElement(
    settings: TableSettings,
    ctx: ContextForKind<RenderContextKind>,
    runtime: RenderRuntime,
    { isSwarmCompatible }: ElementRenderState
): ElementContentRenderResult {
    const { binding } = settings;
    // Preset cards use the editor surface without editor frames or controls.
    const keepEmptyVisible = runtime.renderSurface === 'editor';
    if (!binding || !isSwarmCompatible) {
        const hasRenderableContent = shouldRenderTable(settings, settings, keepEmptyVisible);
        return {
            node: hasRenderableContent ? renderTableLayout(settings, settings) : null,
            ready: Promise.resolve(),
            hasRenderableContent: Promise.resolve(hasRenderableContent),
        };
    }

    const descriptor = getTableRuntimeBindingDescriptorOrThrow(binding);
    function getGraphRuntimeState() {
        if (ctx.kind !== 'measuring-point') {
            throw new Error('Table DataSet requires a measuring-point context.');
        }
        return runtime.getGraphRuntimeState(ctx.measuringPoint);
    }
    const graphRuntimeState = linkNeedsDataSet(descriptor.link)
        ? getGraphRuntimeState()
        : undefined;

    const cellOptions$ = resolveCellRenderOptions$(
        descriptor.link,
        { getDataSet: () => getGraphRuntimeState().dataSet },
        runtime.profile
    );

    const linkValue$ = runtime.observeBinding(binding, { context: ctx });

    const renderedShape$ = combineLatest([linkValue$, cellOptions$]).pipe(
        map(([linkValue, cellOptions]) =>
            buildLinkTableShape(descriptor.link, linkValue, settings, ctx, cellOptions)
        )
    );

    // Start resolving outside React: a page waits for this content result before mounting.
    const initialShape = (
        graphRuntimeState
            ? firstValueFrom(
                  merge(
                      from(graphRuntimeState.dataSet.ready),
                      graphRuntimeState.dataSetRequestError$.pipe(
                          map((err): never => {
                              throw err;
                          })
                      )
                  )
              )
            : Promise.resolve()
    ).then(() => firstValueFrom(renderedShape$));
    const hasRenderableContent = initialShape.then((shape) =>
        shouldRenderTable(settings, shape, keepEmptyVisible)
    );

    return {
        node: (
            <DynamicTableRenderer
                shape$={concat(from(initialShape), renderedShape$)}
                settings={settings}
                keepEmptyVisible={keepEmptyVisible}
            />
        ),
        ready: hasRenderableContent.then(() => undefined),
        hasRenderableContent,
    };
}

export class TableElement extends BaseReportElement<RenderContextKind, TableSettings> {
    static readonly elementType = 'table';

    static readonly layout = { flow: 'flow' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_TABLE_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: createBlankTableDefinition,
            presets: [
                {
                    key: 'blank',
                    label: gettext('ELEMENT_TABLE_PRESET_BLANK_LABEL'),
                    description: gettext('ELEMENT_TABLE_PRESET_BLANK_DESCRIPTION'),
                    compatibleContextKinds: ['project', 'measuring-point'] as const,
                    supportedSwarmTypes: null,
                    createDefinition: createBlankTableDefinition,
                },
                ...createRuntimeTableInsertionPresets(),
            ],
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        const staticDefaults = createDefaultStaticTableSettings();
        return {
            fields: [
                {
                    name: 'title',
                    label: gettext('TITLE'),
                    type: 'char',
                    required: false,
                    default: null,
                },
                {
                    name: 'spacious',
                    label: gettext('USE_SPACIOUS_TABLE_LAYOUT'),
                    type: 'boolean',
                    required: false,
                    default: false,
                },
                createRuntimeBindingGroupField({ includeArg: false }),
                {
                    name: 'header',
                    label: gettext('HEADER_COLUMNS'),
                    type: 'stringList',
                    required: true,
                    minItems: 1,
                    itemLabel: gettext('COLUMN'),
                    default: staticDefaults.header,
                },
                {
                    name: 'columnAlignments',
                    label: gettext('COLUMN_ALIGNMENTS'),
                    type: 'stringList',
                    required: false,
                    itemLabel: gettext('ALIGNMENT_LEFT_CENTER_RIGHT'),
                    default: staticDefaults.columnAlignments,
                },
                {
                    name: 'columnWidths',
                    label: gettext('COLUMN_WIDTHS'),
                    type: 'stringList',
                    required: false,
                    itemLabel: gettext('WIDTH_EXAMPLE'),
                    default: [],
                },
                {
                    name: 'rows',
                    label: gettext('ELEMENT_ROWS_LABEL'),
                    type: 'rows',
                    required: true,
                    default: staticDefaults.rows,
                    maxRows: 50,
                    fields: [
                        {
                            name: 'cells',
                            label: gettext('CELLS'),
                            type: 'stringList',
                            required: true,
                            minItems: 1,
                            itemLabel: gettext('CELL'),
                            default: [],
                        },
                    ],
                },
                {
                    name: 'showWhenEmpty',
                    label: gettext('ELEMENT_TABLE_SHOW_WHEN_EMPTY'),
                    type: 'boolean',
                    required: false,
                    default: false,
                },
            ],
        };
    }

    static async resolveSettingsFormSchema({
        contextKind,
        client,
        currentSettings,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        const schema = resolveRuntimeBindingSettingsFormSchema({
            contextKind,
            settingsFormSchema: TableElement.settingsFormSchema,
            allowEmptyBindingOption: true,
            listDescriptorsForContext: listTableRuntimeBindingsForContext,
        });

        const bindingId = isRecord(currentSettings.binding)
            ? currentSettings.binding.bindingId
            : null;
        const hasActiveBinding = typeof bindingId === 'string' && bindingId.trim().length > 0;

        const schemaForContext = hasActiveBinding
            ? {
                  ...schema,
                  fields: schema.fields.filter(
                      (field) => !TABLE_STATIC_LAYOUT_FIELD_NAMES.has(field.name)
                  ),
              }
            : schema;

        const descriptor = hasActiveBinding
            ? listTableRuntimeBindingsForContext(contextKind).find((d) => d.bindingId === bindingId)
            : undefined;
        const argOptions = descriptor?.link.argOptions;

        if (argOptions?.length) {
            return withRuntimeBindingArgChoiceField(schemaForContext, argOptions);
        }

        if (descriptor?.bindingId === PROJECT_MEASURING_POINTS_BINDING_ID) {
            const projectOptions = await resolveProjectArgOptions({ client });
            return withRuntimeBindingArgChoiceField(schemaForContext, projectOptions);
        }

        return schemaForContext;
    }

    static buildStructureSettingsYupSchema(
        _helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        return buildTableSettingsYupSchema();
    }

    /**
     * `spacious` defaults to off, so storing the default would put a key in every template that
     * says nothing. Dropped on save the way `ImageElement` drops its own inert keys.
     */
    static normalizeSettingsForSave(
        _currentSettings: ElementSettings,
        nextSettings: ElementSettings
    ): ElementSettings {
        if (nextSettings.spacious) {
            return nextSettings;
        }

        const normalizedSettings = { ...nextSettings };
        delete normalizedSettings.spacious;
        return normalizedSettings;
    }

    protected normalizeSettings(settings: TableSettings): TableSettings {
        TableElement.validateSettings(settings);
        return normalizeTableSettings(settings);
    }

    protected getRuntimeBindings() {
        return this.settings.binding ? [this.settings.binding] : [];
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime,
        renderState: ElementRenderState
    ): ElementContentRenderResult {
        return renderTableElement(this.settings, ctx, runtime, renderState);
    }
}
